import { NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { ReportingGraphComponent } from '../reporting-graph/reporting-graph.component';
import { Title } from '@angular/platform-browser';
import { PTicketService } from '../../../services/p-ticket.service';
import { pTicket } from '../../../interfaces/pTicket';
import { PTicketFilterComponent } from '../../pTicket/p-ticket-filter/p-ticket-filter.component';
import { HttpParams } from '@angular/common/http';
import { Chart, ChartType, registerables } from 'chart.js';
import { FormsModule, NgModel } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { Filter } from '../../../interfaces/filter';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { report, xAxisOption } from '../../../interfaces/preport';
import { ReportService } from '../../../services/report.service';
import { openSnackBar } from '../../../helpers/genericFunctions';
import {
  MatProgressSpinner,
  MatProgressSpinnerModule,
} from '@angular/material/progress-spinner';
import {
  MatChip,
  MatChipGrid,
  MatChipSet,
  MatChipsModule,
} from '@angular/material/chips';
import { NewReportDialogComponent } from '../new-report-dialog/new-report-dialog.component';
import { MatTooltip } from '@angular/material/tooltip';
import { HeaderTitleService } from '../../../services/header-title.service';
import { globalUser } from '../../../helpers/globalFields';
@Component({
  selector: 'app-reporting-overview',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    NgFor,
    ReportingGraphComponent,
    FormsModule,
    MatChip,
    NgIf,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatTooltip,
  ],
  templateUrl: './reporting-overview.component.html',
  styleUrl: './reporting-overview.component.scss',
})
export class ReportingOverviewComponent {
  reports: report[] = [];
  report!: report;
  xAxisOptions: xAxisOption[] = [
    { key: 'pct_has_solution', label: 'Has Solution' },
    { key: 'pct_supportlevel', label: 'Support Level' },
    { key: 'pct_status', label: 'Status' },
    { key: 'pct_tickettype', label: 'Ticket Type' },
    { key: 'pct_company', label: 'Company' },
    { key: 'pct_countrycode', label: 'Country' },
    { key: 'pct_issuetype', label: 'Issue Type' },
    { key: 'pct_severity', label: 'Severity' },
    { key: 'pct_component', label: 'Component' },
    { key: 'pct_status_granulated', label: 'Granulated status' },
    { key: 'pkeywords', label: 'Keywords' },
    { key: 'pct_organization', label: 'Organization' },
  ];
  @ViewChildren(ReportingGraphComponent)
  reportingGraphComponents!: QueryList<ReportingGraphComponent>;

  constructor(
    private pTicketService: PTicketService,
    private pReportService: ReportService,
    private headerTitleService: HeaderTitleService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.headerTitleService.setTitle('Reports');
    globalUser.getUserStream().subscribe((user) => {
      if (user) {
        this.loadReports();
      }
    });
  }
  isLoading = false;
  loadReports(): void {
    this.isLoading = true;

    this.pReportService.getAll().subscribe((reports) => {
      this.reports = reports;
      this.isLoading = false;
    });
  }
  getFilterLabel(key: string): string {
    const option = this.xAxisOptions.find((opt) => opt.key === key);
    return option ? option.label : key;
  }

  formatDate(value: string): string {
    const date = new Date(value);
    return isNaN(date.getTime()) ? value : date.toLocaleDateString();
  }

  onAddReport(report?: report): void {
    const dialogRef = this.dialog.open(NewReportDialogComponent, {
      data: { report: report },
      maxWidth: '800px',
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result: report | undefined) => {
      if (!result) return;

      this.pReportService.getById(result.id).subscribe((newReport) => {
        if (!newReport) return;

        // Check if it already exists in the array
        const index = this.reports.findIndex((r) => r.id === newReport.id);

        if (index > -1) {
          // It's an update, replace in-place
          this.reports[index] = newReport;
        } else {
          // It's a new report, push it into the array
          this.reports.push(newReport);
        }
      });
    });
  }

  onExportReport(report: report): void {
    // Find the ReportingGraphComponent that corresponds to this report
    const targetComponent = this.reportingGraphComponents.find(
      (comp) => comp.report === report,
    );
    if (targetComponent) {
      const labels = targetComponent.dataset.label;
      const values = targetComponent.dataset.data;
      const csvContent = [labels.join(','), values.join(',')].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.rep_title || 'report'}.csv`;
      a.click();

      URL.revokeObjectURL(url);
    } else {
      return;
    }
  }

  onDuplicateReport(report: report): void {
    let duplicatedReport: report = { ...report };
    duplicatedReport.rep_title = report.rep_title + ' - copy';
    duplicatedReport.id = -1;
    this.pReportService.create(duplicatedReport).subscribe((result) => {
      openSnackBar(
        'Report copied successfully',
        2000,
        this.pReportService['snackBar'],
      );
      this.pReportService.getById(result.id).subscribe((newReport) => {
        if (!newReport) return;

        // Check if it already exists in the array
        const index = this.reports.findIndex((r) => r.id === newReport.id);

        if (index > -1) {
          // It's an update, replace in-place
          this.reports[index] = newReport;
        } else {
          // It's a new report, push it into the array
          this.reports.push(newReport);
        }
      });
    });
  }

  onDeleteReport(report: report): void {
    this.pReportService.delete(report.id).subscribe({
      next: () => {
        // Remove the deleted report from the local array
        this.reports = this.reports.filter((r) => r.id !== report.id);

        openSnackBar(
          'Report deleted successfully',
          2000,
          this.pReportService['snackBar'],
        );
      },
      error: (err) => {
        openSnackBar(
          'Failed to delete report',
          5000,
          this.pReportService['snackBar'],
        );
        console.error(err);
      },
    });
  }
  exportChartToPDF(report: report): void {
    if (!report.canvas || !report.canvas.nativeElement) {
      console.error('Chart canvas not available!');
      return;
    }

    html2canvas(report.canvas.nativeElement).then((canvas) => {
      let doc = new jsPDF();
      const chartWidth = canvas.width;
      const chartHeight = canvas.height;
      doc = new jsPDF('l', 'px', [chartWidth, chartHeight]);
      const imgData = canvas.toDataURL('image/svg');
      doc.addImage(imgData, 'SVG', 0, 0, chartWidth, chartHeight);
      doc.save(`${report.rep_title}.pdf`);
    });
  }
}

export interface dataset {
  label: any[];
  data: number[];
}

function objectToHttpParams(
  obj: Record<string, string | string[]>,
): HttpParams {
  let params = new HttpParams();
  Object.entries(obj).forEach(([key, val]) => {
    if (Array.isArray(val)) {
      val.forEach((v) => (params = params.append(key, v)));
    } else {
      params = params.set(key, val);
    }
  });
  return params;
}

export interface dataset {
  label: any[];
  data: number[];
}
